import React from "react";
import { AuthProvider, AuthProviderProps, useAuth } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import { FLEET_STATE_CONFIGURED } from "../utils/devFlags";
import { urlFromEnv } from "../utils/urlEnv";

/**
 * Cognito (fleet_state pool) OIDC config for the Tractor State page.
 *
 * Interim, page-scoped auth (SW-6110): the /tractor-state route logs in against
 * the fleet_state Cognito pool via hosted-UI authorization-code + PKCE (public
 * SPA client, no secret) and uses the resulting ID token as the bearer for the
 * read API. It is deliberately isolated from the app's Auth0 login so it can
 * later be LIFTED to wrap the whole app (the Auth0 -> Cognito migration) by
 * moving <FleetStateAuthProvider> up to index.tsx — no rewrite here.
 *
 * All values are public (client id, pool id, hosted-UI domain), so they ride as
 * plain REACT_APP_* env vars (the committed `.env.beta` / `.env.prod` in the
 * deploys, `.env` locally), not secrets. There is deliberately NO hardcoded fallback: a build
 * missing these shows a "not configured" notice rather than silently pointing a
 * prod bundle at the beta pool.
 */
// Normalised — see utils/urlEnv for the double-slash sign-out this prevents.
const AUTHORITY = urlFromEnv(
  process.env.REACT_APP_FLEET_STATE_COGNITO_AUTHORITY,
);
const CLIENT_ID = process.env.REACT_APP_FLEET_STATE_COGNITO_CLIENT_ID;
const HOSTED_UI = urlFromEnv(process.env.REACT_APP_FLEET_STATE_COGNITO_DOMAIN);

// Built only when FLEET_STATE_CONFIGURED, so the env values are guaranteed
// present. That check lives in utils/devFlags because the route decides on it
// too — it is what picks the live page over the isolated-dev one.
function buildOidcConfig(): AuthProviderProps {
  const authority = AUTHORITY as string;
  const hostedUi = HOSTED_UI as string;
  return {
    authority,
    client_id: CLIENT_ID as string,
    // Must exactly match an allowed callback on the SPA client (Terraform
    // app_callback_urls). We return to the page itself; the provider completes
    // the code exchange on mount and strips ?code=&state= (onSigninCallback).
    redirect_uri: `${window.location.origin}/tractor-state`,
    post_logout_redirect_uri: window.location.origin,
    response_type: "code",
    // Only the OIDC scopes are requested. The resource-server scopes that
    // actually authorize API calls are minted by the pre-token-generation
    // trigger from the caller's groups, and anything unentitled is suppressed
    // there — deliberately NOT requested here, because allowed_oauth_scopes is
    // per-client, so a request for `.../admin` would otherwise be granted to
    // whoever asked.
    scope: "openid email profile",
    // No forced identity_provider. Staff pick "Continue with Google"; external
    // users — every contractor and non-federated customer — need the hosted
    // UI's password form, which skipping the picker made unreachable.
    // Cognito's authorize/token endpoints live on the hosted-UI domain while
    // the issuer/JWKS live on cognito-idp; pin them to avoid discovery quirks.
    metadata: {
      issuer: authority,
      authorization_endpoint: `${hostedUi}/oauth2/authorize`,
      token_endpoint: `${hostedUi}/oauth2/token`,
      userinfo_endpoint: `${hostedUi}/oauth2/userInfo`,
      end_session_endpoint: `${hostedUi}/logout`,
      jwks_uri: `${authority}/.well-known/jwks.json`,
    },
    automaticSilentRenew: true,
    // Cognito has no token-revocation endpoint for this client; don't call one.
    revokeTokensOnSignout: false,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // Drop the auth code from the URL once the exchange completes.
    onSigninCallback: () => {
      window.history.replaceState({}, document.title, "/tractor-state");
    },
  };
}

/**
 * Shown when a build can reach neither the API nor a snapshot.
 *
 * Every deployed build is expected to be configured; this is what an
 * unconfigured one says instead of inventing a fleet to render.
 */
export function FleetStateNotConfigured() {
  return (
    <div style={{ padding: 40, color: "#222a35" }}>
      Tractor State isn’t configured for this environment — set
      REACT_APP_FLEET_STATE_API_URL and the three
      REACT_APP_FLEET_STATE_COGNITO_* values.
    </div>
  );
}

export function FleetStateAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  if (!FLEET_STATE_CONFIGURED) {
    // Fail visibly, and never fall back to a baked-in pool. Doesn't crash the
    // rest of the app (this provider only wraps /tractor-state). The route
    // gates on the same constant, so reaching this is a programming error.
    // eslint-disable-next-line no-console
    console.error(
      "fleet_state Cognito not configured: set REACT_APP_FLEET_STATE_COGNITO_AUTHORITY / _CLIENT_ID / _DOMAIN",
    );
    return <FleetStateNotConfigured />;
  }
  return <AuthProvider {...buildOidcConfig()}>{children}</AuthProvider>;
}

export { useAuth as useFleetStateAuth };
